import mustache from "mustache";
import {
  fx_createAplicacionRecipienteRequest,
  fx_createAplicacionRecipienteRequestEmpty,
  AplicacionRecipienteRequest,
} from "../../../../../modulos/aplicaciones/request/AplicacionRecipienteRequest";
import { AplicacionRecipienteResponse } from "../../../../../modulos/aplicaciones/response/AplicacionRecipienteResponse";
import { AplicacionRecipienteSaveGrpcService } from "../../../../../modulos/aplicaciones/services/AplicacionRecipienteSaveGrpcService";
import { BaseCreateRender } from "../../../../base/BaseCreateRender";
import { FormCardRender } from "../../../../implementations/card/FormCardRender";
import { InputNumberField } from "../../../../implementations/form/number/InputNumberField";
import { TextField } from "../../../../implementations/form/text/TextField";
import template from "./recipienteFormModalTemplate.html";

mustache.parse(template);

/** Litros máximos que se aceptan. No es un límite del dominio sino un filtro de
 *  errores de digitación: un recipiente de más de diez mil litros no se carga a
 *  mano por lote, y un cero de más en este campo multiplicaría por diez toda la
 *  mezcla calculada de la aplicación. */
const LITROS_MAX = 10_000;

/** Modal chico de crear/editar un recipiente de aplicación (auto-montado sobre
 *  `body`): nombre y litros. `recipiente` null = crear; con valor = editar.
 *
 *  Todo recipiente es del cliente, así que cualquiera se puede editar — también
 *  los dos estándar con los que arranca la lista, que son copias suyas. */
export class RecipienteFormModal extends BaseCreateRender {
  protected readonly elementId = "recipienteFormModal";

  private readonly _formCard: FormCardRender<
    AplicacionRecipienteRequest,
    AplicacionRecipienteResponse
  >;

  /**
   * @param clienteId   Cliente dueño del recipiente (para crear).
   * @param recipiente  Recipiente a editar, o null para crear uno nuevo.
   * @param onSaveEnd   Notifica el recipiente creado/editado.
   */
  constructor(
    clienteId: string,
    recipiente: AplicacionRecipienteResponse | null,
    onSaveEnd: (recipiente: AplicacionRecipienteResponse) => void,
  ) {
    super();
    const request = recipiente
      ? fx_createAplicacionRecipienteRequest(recipiente)
      : fx_createAplicacionRecipienteRequestEmpty(clienteId);
    this._formCard = new FormCardRender<
      AplicacionRecipienteRequest,
      AplicacionRecipienteResponse
    >(request, {
      cardHeader: {
        title: recipiente ? "Editar recipiente" : "Nuevo recipiente",
      },
      createFormFields: (req) => [
        this._createNombre(req),
        this._createLitros(req),
      ],
      saveService: new AplicacionRecipienteSaveGrpcService(),
      submitText: "Guardar",
      cancelAction: () => this.dispose(),
      onSubmitEnd: (saved) => {
        onSaveEnd(saved);
        this.dispose();
      },
    });
  }

  protected getHtml(): string {
    return mustache.render(template, { card: this._formCard.render() });
  }

  protected afterInit(element: HTMLDivElement): void {
    this._formCard.connect();
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._overlay?.addEventListener("click", this._onOverlayClick);
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  protected beforeDispose(_element: HTMLDivElement): void {
    this._overlay?.removeEventListener("click", this._onOverlayClick);
    this._formCard.dispose();
  }

  private _overlay: HTMLDivElement | null = null;

  private _onOverlayClick = (e: MouseEvent): void => {
    if (e.target === this._overlay) this.dispose();
  };

  private _createNombre(
    request: AplicacionRecipienteRequest,
  ): TextField<AplicacionRecipienteRequest> {
    return new TextField<AplicacionRecipienteRequest>(
      request,
      {
        label: "Nombre",
        type: "text",
        description: "Como se elige en el formulario: “Caneca 200 L”.",
        get: (r) => r.nombre,
        set: (r, value) => {
          r.nombre = value;
        },
      },
      { isRequired: true, maxLength: 60 },
    );
  }

  private _createLitros(
    request: AplicacionRecipienteRequest,
  ): InputNumberField<AplicacionRecipienteRequest> {
    return new InputNumberField<AplicacionRecipienteRequest>(
      request,
      {
        label: "Litros",
        description:
          "Cuántos litros de mezcla le caben. Es el factor que convierte las canecas de cada lote en litros.",
        placeholder: "200",
        // Decimal: hay recipientes que no son redondos (55 galones = 208,2 L) y
        // redondear el factor desviaría la mezcla de toda la aplicación.
        decimal: true,
        get: (r) => r.litros,
        set: (r, value) => {
          r.litros = value;
        },
      },
      // `min: 1` y no 0: el campo vacío vale 0 (modelo de `InputNumberField`),
      // así que un mínimo de 0 dejaría guardar un recipiente sin litros, que es
      // justo lo que el server rechaza.
      { isRequired: true, min: 1, max: LITROS_MAX },
    );
  }
}
